/**
 * NotificationBell — bell icon with unread badge + dropdown panel
 */
import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, Check, CheckCheck } from 'lucide-react';
import { useNotifications } from '../hooks/useNotifications';
import { timeAgo } from '../utils/formatTime';

/**
 * Normalise a notification URL to a route the SPA can actually resolve.
 *
 * Backend notification URLs are stored app-relative (e.g. "/agents?escalation=…")
 * but the app renders under /app/* — navigating to a bare "/agents" misses the
 * router and the catch-all bounces the user to the public landing page
 * (owner report 2026-10-08: "answering brings me to a page of the website").
 */
export function resolveNotificationUrl(url?: string | null): string {
  const u = (url || '').trim();
  if (!u) return '/app';
  if (u.startsWith('/app')) return u;
  if (u.startsWith('/')) {
    // Absolute external links pass through untouched.
    if (u.startsWith('//')) return u;
    return `/app${u}`;
  }
  if (/^https?:\/\//i.test(u)) return u;
  return `/app/${u}`;
}

export default function NotificationBell() {
  const { notifications, unreadCount, markAsRead, markAllRead, connected } = useNotifications();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    function handler(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleClick = (notif: any) => {
    markAsRead(notif.id);
    setOpen(false);
    if (notif.url) {
      navigate(resolveNotificationUrl(notif.url));
    }
  };

  const typeColor = (type: string) => {
    switch (type) {
      case 'action_needed':
        return 'border-l-amber-500';
      case 'operation_complete':
        return 'border-l-emerald-500';
      default:
        return 'border-l-slate-600';
    }
  };

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        className="relative cursor-pointer rounded-lg p-2 text-slate-700 hover:bg-slate-100 dark:text-slate-200 dark:hover:bg-slate-800"
        aria-label="Notifications"
      >
        <Bell size={20} />
        {unreadCount > 0 && (
          <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
        {/* Connected indicator dot */}
        {connected && (
          <span className="absolute bottom-1 right-1 h-1.5 w-1.5 rounded-full bg-emerald-400" aria-hidden="true" />
        )}
      </button>

      {/* Panel stays on screen: right-aligned under the bell on desktop; on
          phones `mobile-responsive.css` pins it to the viewport (left/right
          margins, max-width calc(100vw - 1rem)). */}
      {open && (
        <div role="menu" aria-label="Notifications" className="dropdown-panel absolute right-0 mt-2 max-h-[28rem] w-[min(20rem,calc(100vw-1rem))] max-w-[calc(100vw-1rem)] overflow-hidden rounded-xl border border-slate-300 bg-white shadow-2xl dark:border-slate-600 dark:bg-slate-800">
          {/* Header */}
          <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3 dark:border-slate-600">
            <span className="text-sm font-semibold text-slate-900 dark:text-white">Notifications</span>
            {unreadCount > 0 && (
              <button
                onClick={markAllRead}
                aria-label="Mark all notifications as read"
                className="flex cursor-pointer items-center gap-1 text-xs font-medium text-brand-600 hover:text-brand-700 dark:text-brand-300 dark:hover:text-brand-200"
              >
                <CheckCheck size={14} />
                Mark all read
              </button>
            )}
          </div>

          {/* List */}
          <div className="max-h-96 overflow-y-auto">
            {notifications.length === 0 ? (
              <p className="px-4 py-8 text-center text-sm text-slate-600 dark:text-slate-300">
                No notifications yet
              </p>
            ) : (
              notifications.slice(0, 20).map((notif) => (
                <button
                  key={notif.id}
                  onClick={() => handleClick(notif)}
                  role="menuitem"
                  className={`flex w-full cursor-pointer flex-col gap-1 border-b border-l-2 border-b-slate-200 px-4 py-3 text-left dark:border-b-slate-700 ${typeColor(notif.type)} hover:bg-slate-100 dark:hover:bg-slate-700 ${
                    !notif.read ? 'bg-slate-50 dark:bg-slate-700/70' : 'bg-white dark:bg-slate-800'
                  }`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <span className={`text-sm font-semibold ${notif.read ? 'text-slate-700 dark:text-slate-200' : 'text-slate-950 dark:text-white'}`}>
                      {notif.title}
                    </span>
                    {!notif.read && (
                      <span className="mt-1 h-2 w-2 flex-shrink-0 rounded-full bg-brand-500" />
                    )}
                  </div>
                  {notif.body && (
                    <span className="line-clamp-2 text-xs leading-5 text-slate-600 dark:text-slate-300">{notif.body}</span>
                  )}
                  <span className="text-[10px] font-medium text-slate-600 dark:text-slate-400">{timeAgo(notif.created_at)}</span>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
